import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, IconButton, Tooltip } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import CreateIcon from '@material-ui/icons/Create';
import ToggleOffIcon from '@material-ui/icons/ToggleOff';
import ToggleOnIcon from '@material-ui/icons/ToggleOn';
import { UseMutationOptions } from 'react-query';

import { UseCheckedState } from 'types/app';
import { Alert } from 'components';
import { useNotify } from 'hooks';
import { generateLink } from 'Links';
import {
  DeleteAssignmentMutation,
  DeleteAssignmentMutationVariables,
  useDeleteAssignmentMutation,
  useUpdateAssignmentMutation,
} from 'api';
import useUpdateAssignment from 'features/admin/updaters/useUpdateAssignment';

enum Action {
  Disable,
  Edit,
  Delete,
  None,
}

type DeleteMutationOptions = UseMutationOptions<
  DeleteAssignmentMutation,
  unknown,
  DeleteAssignmentMutationVariables
>;

type Variables = DeleteAssignmentMutationVariables;

interface Props extends Partial<UseCheckedState> {
  deleteMutationOptions: DeleteMutationOptions;
  variables: Variables;
  count: number;
  isActive: boolean;
  title: string;
  bulk?: boolean;
}

const Actions: FC<Props> = ({
  deleteMutationOptions,
  resetCheckedState,
  count,
  isActive,
  bulk,
  variables,
  title,
}) => {
  const { t } = useTranslation();
  const notify = useNotify();

  const [action, setAction] = useState<Action>(Action.None);

  const { mutate: deleteAssignmentMutation } = useDeleteAssignmentMutation({
    ...deleteMutationOptions,
    onSuccess() {
      notify.info({ message: t('admin:assignmentDeleteMessage') });
    },
  });

  const updateAssignmentOptions = useUpdateAssignment(variables);
  const { mutate: updateAssignment } = useUpdateAssignmentMutation({
    ...updateAssignmentOptions,
    onSuccess(data) {
      const { updateAssignment } = data;
      notify.info({
        message: updateAssignment.active
          ? t('admin:assignmentEnabledMessage', { assignment: updateAssignment.title })
          : t('admin:assignmentDisabledMessage', { assignment: updateAssignment.title }),
      });
    },
  });

  function handleCancel() {
    setAction(Action.None);
  }

  function handleBulkDeleteInvitations() {
    deleteAssignmentMutation(variables);
    setAction(Action.None);
    resetCheckedState();
  }

  function handleActiveButtonClick() {
    setAction(Action.None);
    resetCheckedState();

    updateAssignment({
      ...variables,
      data: {
        active: !isActive,
        title,
      },
    });
  }

  return (
    <Fragment>
      {!bulk && (
        <Alert
          destructive
          open={action === Action.Delete}
          onConfirm={handleBulkDeleteInvitations}
          onCancel={handleCancel}
          message={t('admin:assignmentRemoval', { count })}
          confirm={t('common:delete')}
        />
      )}
      {isActive && (
        <Alert
          open={action === Action.Disable}
          onConfirm={handleActiveButtonClick}
          onCancel={handleCancel}
          message={t('admin:assignmentToggleAlert', { count })}
          confirm={t('common:disable')}
        />
      )}
      <Grid container alignItems="center" justify="flex-end">
        <Grid item>
          <Tooltip title={t('common:delete')}>
            <IconButton onClick={() => setAction(Action.Delete)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Grid>
        {!bulk && (
          <Grid item>
            <Tooltip title={t('common:edit')}>
              <IconButton
                onClick={() => setAction(Action.Edit)}
                component={generateLink(`/admin/settings/assignments/${variables.id}`)}
              >
                <CreateIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        )}
        {isActive && (
          <Grid item>
            <Tooltip title={t('common:disable')}>
              <IconButton onClick={() => setAction(Action.Disable)}>
                <ToggleOffIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        )}
        {!isActive && (
          <Grid item>
            <Tooltip title={t('common:enable')}>
              <IconButton onClick={handleActiveButtonClick}>
                <ToggleOnIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        )}
      </Grid>
    </Fragment>
  );
};

Actions.defaultProps = {
  bulk: false,
};

export default Actions;
